import { useCallback, useMemo } from 'react'
import { useMutation } from '@apollo/client/react'
import {
  AdminAPI,
  getFragment,
  GraphQL,
  SharedAPI,
  utils
} from '@guestgoodie/graphql'
import { Trans, useLingui } from '@lingui/react/macro'
import { Key01Icon } from '@hugeicons/core-solid-rounded'

import * as Table from '@/components/ui/Table'
import Loading from '@/components/ui/Loading'
import usePaginatedStays, { QueryVariables } from '../hooks/usePaginatedStays'
import StayRow from './StayRow'
import { useIsPmsConnected } from '@/features/integrations'
import { Permission, useCheckPermission } from '@/features/auth'

function LoadingRow() {
  return (
    <Table.Row className="pointer-events-none">
      <Table.Cell colSpan={6}>
        <div className="flex flex-row justify-center p-4">
          <Loading />
        </div>
      </Table.Cell>
    </Table.Row>
  )
}

interface SpaceTableProps {
  variables: QueryVariables
  onChangeSort: (
    field: GraphQL.StaySortField,
    direction: GraphQL.SortDirection
  ) => void
}

export default function StayTable({
  variables,
  onChangeSort
}: SpaceTableProps) {
  const {
    items: stays,
    page,
    metadata,
    loading,
    loadPage
  } = usePaginatedStays(variables)

  const { t } = useLingui()

  const hasPmsConnected = useIsPmsConnected()
  const hasDeletePermission = useCheckPermission([Permission.STAY_DELETE])
  const hasUpdatePermission = useCheckPermission([Permission.STAY_UPDATE])
  const canDelete = hasDeletePermission && !hasPmsConnected
  const canUpdate = hasUpdatePermission && !hasPmsConnected

  const [deleteStay] = useMutation(AdminAPI.stay.DELETE, {
    update(cache, { data }) {
      if (!data) return

      const stay = getFragment(SharedAPI.stay.STAY_FRAGMENT, data.deleteStay)

      utils.paginationHelpers.remove(cache, stay.id, 'Stay', 'queryStays')
    }
  })

  const { direction, field } = variables.sort

  const { totalPages, totalEntries } = useMemo(() => {
    return {
      totalPages: metadata?.totalPages,
      totalEntries: metadata?.totalEntries
    }
  }, [metadata])

  const hasResults = useMemo(
    () => !!totalEntries && totalEntries > 0,
    [totalEntries]
  )

  const onDeleteStay = useCallback(
    (id: number) => {
      deleteStay({ variables: { id } })
    },
    [deleteStay]
  )

  return (
    <Table.Root>
      <Table.Head>
        <Table.Row>
          <Table.Heading>
            <Trans>Connected Room</Trans>
          </Table.Heading>
          <Table.SortableHeading<GraphQL.StaySortField>
            sortField={GraphQL.StaySortField.ScheduledStart}
            currentSortDirection={direction}
            currentSortField={field}
            onChangeSort={onChangeSort}
          >
            <Trans>From</Trans>
          </Table.SortableHeading>
          <Table.SortableHeading<GraphQL.StaySortField>
            sortField={GraphQL.StaySortField.ScheduledStop}
            currentSortDirection={direction}
            currentSortField={field}
            onChangeSort={onChangeSort}
          >
            <Trans>Until</Trans>
          </Table.SortableHeading>
          <Table.Heading>
            <Trans>Guests</Trans>
          </Table.Heading>
          <Table.SortableHeading<GraphQL.StaySortField>
            sortField={GraphQL.StaySortField.State}
            currentSortDirection={direction}
            currentSortField={field}
            onChangeSort={onChangeSort}
          >
            <Trans>Status</Trans>
          </Table.SortableHeading>
          <Table.Heading />
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {stays &&
          stays.map(stay => {
            return (
              <StayRow
                key={stay.id}
                stay={stay}
                showUpdate={canUpdate}
                showDelete={canDelete}
                onDelete={() => onDeleteStay(stay.id)}
              />
            )
          })}
        {!hasResults && !loading && (
          <Table.Empty text={t`No Stays found`} Icon={Key01Icon} />
        )}
        {loading && <LoadingRow />}
      </Table.Body>
      {hasResults && page && (
        <Table.PaginationCaption
          totalEntries={totalEntries!}
          currentPage={page}
          totalPages={totalPages!}
          onChangePage={loadPage}
        />
      )}
    </Table.Root>
  )
}
